import React, { useState, useMemo, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import {
  Checkbox,
  SubmitButton,
  CancelButton,
  SimpleButton,
  MultiSelect
} from '../../form-elements';
import { FacilityForm } from './FacilityForm';
import { Credentials, Specialties, AgeOfPatients } from '../formSelectOptions';
import FORM_CONFIG, { getFormFieldRules } from '../formConfig';
import checkIcon from '@images/icons/check-mark-identify.svg'
import facilitySearchDropdown from '@images/icons/select-icon.svg'
import Modal from '@components/Modal/Modal';
import { getFacilityFieldValidation } from './FacilityForm';
import GTM from '@utils/GTM';

const StepTwo = ({ 
  prevStep, 
  hasSpecialtyErrors,
  hasFacilityErrors,
  hasAgreementErrors,
  stateSelectRefs
}) => {
  const { 
    register, 
    formState: { errors },
    watch,
    setValue,
    getValues,
    clearErrors,
    trigger,
    control,
    unregister
  } = useFormContext();

  // Use useFieldArray for managing facilities
  const { fields, append, remove } = useFieldArray({
    control,
    name: "facilities"
  });

  const watchFirstName = watch('firstName');
  const watchMiddleName = watch('middleName');
  const watchLastName = watch('lastName');
  const watchNPI = watch('npiNumber');
  const watchEmail = watch('email');
  const [showFacilitySearch, setShowFacilitySearch] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showFacilityInfo, setShowFacilityInfo] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  
  // Track confirmed facilities
  const confirmedFacilities = watch('confirmedFacilities') || [];

  useEffect(() => {
    if (showFacilitySearch || confirmedFacilities.length >= 6) {  // match when form should be hidden
      // Unregister all facility fields when the form is hidden
      fields.forEach((field, index) => {
        // Unregister all subfields
        const fieldsToUnregister = [
          'hco_entity_id',
          'facilitySearch',
          'address',
          'city',
          'state',
          'zipCode',
          'phoneNumber',
          'facilityWebAddress',
          'procedures'
        ];
        
        fieldsToUnregister.forEach(fieldName => {
          unregister(`facilities.${index}.${fieldName}`);
        });
      });
    }
  }, [showFacilitySearch, fields, unregister]);

  const handleConfirmAddress = async () => {
    // Get the latest facility index
    const currentIndex = fields.length - 1;
    const facilityPrefix = `facilities.${currentIndex}`;
    
    // Register validation rules for the fields just before validating
    const fieldsToValidate = [
      'facilitySearch',
      'address',
      'city',
      'state',
      'zipCode',
      'phoneNumber',
    ];
    
    // Trigger validation for all fields
    const isValid = await trigger(fieldsToValidate.map(field => `${facilityPrefix}.${field}`));
    
    // Track field validation for facility fields
    const currentErrors = control._formState.errors;
    fieldsToValidate.forEach(fieldName => {
      const fullFieldName = `${facilityPrefix}.${fieldName}`;
      const errorValue = currentErrors?.facilities?.[currentIndex]?.[fieldName]?.message || 'Valid';

      if (errorValue && errorValue !== 'Valid') {
        GTM.fieldValidation('Surgeon Selector', fullFieldName, errorValue);
      }
    });

    if (!isValid) {
      return;
    }
    
    // Get the current facility data
    const currentFacilityData = {
      id: fields[currentIndex].id,
      hco_entity_id: getValues(`${facilityPrefix}.hco_entity_id`) || '',
      facilityName: getValues(`${facilityPrefix}.facilitySearch`) || 'New Facility',
      address: getValues(`${facilityPrefix}.address`) || '',
      city: getValues(`${facilityPrefix}.city`) || '',
      state: getValues(`${facilityPrefix}.state`) || '',
      zipCode: getValues(`${facilityPrefix}.zipCode`) || '',
      phoneNumber: getValues(`${facilityPrefix}.phoneNumber`) || '',
      procedures: getValues(`${facilityPrefix}.procedures`) || []
    };
    
    // Add to confirmed facilities in the form data
    const updatedConfirmedFacilities = [...confirmedFacilities, currentFacilityData];
    setValue('confirmedFacilities', updatedConfirmedFacilities);
    
    // Clear any validation errors for confirmedFacilities since we now have at least one facility
    clearErrors('confirmedFacilities');
    
    // Clear fields for this facility
    ['hco_entity_id', 'facilitySearch', 'address', 'city', 'state', 'zipCode', 'phoneNumber', 'procedures'].forEach(field => {
      setValue(`${facilityPrefix}.${field}`, '', { shouldValidate: false });
    });
    
    // Clear any validation errors
    clearErrors(facilityPrefix);
    
    // Add a new facility if under limit
    if (fields.length < 6) {
      append({ id: Date.now() }); // Use timestamp to ensure unique IDs
    }

    // Completely remove the facility search section from HTML
    setShowFacilitySearch(true);

    // Remove focus from any active form element
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Check if a facility has been confirmed
  const isFacilityConfirmed = (facilityId) => {
    return confirmedFacilities.some(f => f.id === facilityId);
  };

  // Remove a confirmed facility
  const removeConfirmedFacility = (facilityId) => {
    // Remove from confirmedFacilities array
    const updatedFacilities = confirmedFacilities.filter(f => f.id !== facilityId);
    setValue('confirmedFacilities', updatedFacilities);
    
    // Find and remove the corresponding field from fields array
    const fieldIndex = fields.findIndex(field => field.id === facilityId);
    if (fieldIndex !== -1) {
      remove(fieldIndex);
    }
    
	  // Check if there's at least one unconfirmed facility form available
	  const hasUnconfirmedFacility = fields.some(field => !isFacilityConfirmed(field.id));

	  // Only add a new form if there are no unconfirmed forms available
	  if (!hasUnconfirmedFacility) {
      // Add a new empty facility form
      append({ id: Date.now() });
	  }

	  // Show the facility form section if it was hidden
	  setShowFacilitySearch(false);
  };

  // Handle cancel button click to show confirmation modal
  const handleCancelClick = () => {
    setShowResetConfirm(true);
  };

  // Close the confirmation modal
  const closeResetConfirm = () => {
    setShowResetConfirm(false);
  };

  // Confirm reset and reload the page
  const confirmReset = () => {
    window.location.reload();
  };


  // Update this function to include the index
  const handleFullFacilityInfo = (facility, index) => {
    setSelectedFacility({...facility, index: index + 1});
    setShowFacilityInfo(true);
  };

  // Add this function to close the facility info modal
  const closeFacilityInfo = () => {
    setShowFacilityInfo(false);
    setSelectedFacility(null);
  };

  return (
    <div className="grid grid-cols-1 gap-12">
      {/* Specialty and Credentials Section */}
      <section 
        id={FORM_CONFIG.sections.specialty.id} 
        aria-label="Specialty and Credentials" 
        className="bg-white lg:py-12 pt-12 pb-12 lg:pb-14 px-6 lg:p-14 rounded-all_48"
      >
        <h2 
          className="text-p_medium lg:text-h4_medium text-dark_green lg:mb-12 mb-8 text-center" 
          id={FORM_CONFIG.sections.specialty.id}
        >
          {FORM_CONFIG.sections.specialty.title}
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-10 gap-6 lg:px-[73px]">
          {/* Left Column - Personal Info Summary */}
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center gap-2.5">
                <span className="lg:text-[16px] text-[14px] leading-[18px] font-bold text-dark_green">First Name:</span>
                <span className="text-p font-regular text-black/70">{watchFirstName}</span>
              </div>
              
              {watchMiddleName && (
                <div className="flex items-center gap-2.5">
                  <span className="lg:text-[16px] text-[14px] leading-[18px] font-bold text-dark_green">Middle Name:</span>
                  <span className="text-p font-regular text-black/70">{watchMiddleName}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2.5">
                <span className="lg:text-[16px] text-[14px] leading-[18px] font-bold text-dark_green">Last Name:</span>
                <span className="text-p font-regular text-black/70">{watchLastName}</span>
              </div>
              
              <div className="flex items-center gap-2.5">
                <span className="lg:text-[16px] text-[14px] leading-[18px] font-bold text-dark_green">NPI:</span>
                <span className="text-p font-regular text-black/70">{watchNPI}</span>
              </div>
              
              <div className="flex items-center flex-wrap lg:flex-nowrap gap-2.5">
                <span className="lg:text-[16px] text-[14px] leading-[18px] font-bold text-dark_green flex-[1_100%] lg:flex-none">Email Address:</span>
                <span className="text-p font-regular text-black/70">{watchEmail}</span>
                <span 
                  className="text-p font-bold text-dark_green cursor-pointer"
                  onClick={prevStep}
                >
                  [<span className="underline">change</span>]
                </span>
              </div>
            </div>
          </div>
          
          {/* Right Column - Specialty & Credentials */}
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-6">
              <MultiSelect
                label="Credentials"
                name="credentials"
                placeholder="Credentials"
                additionalText="(Select All That Apply)*"
                id="credentials"
                options={useMemo(() => Object.entries(Credentials).map(([value, data]) => ({
                  value,
                  label: data.label
                })), [])}
                error={errors.credentials?.message}
                required={true}
                {...register('credentials', getFormFieldRules('credentials'))}
              />
              
              <MultiSelect
                label="Age of Patients"
                name="ageOfPatients"
                placeholder="Age of Patients"
                additionalText="(Select All That Apply)*"
                id="ageOfPatients"
                options={useMemo(() => AgeOfPatients, [])}
                error={errors.ageOfPatients?.message}
                required={true}
                {...register('ageOfPatients', getFormFieldRules('ageOfPatients'))}
              />
              
              {/* TODO: filter specialties based on patient age (see reference doc) */}
              <MultiSelect
                label="Specialty"
                name="specialties"
                placeholder="Specialty"
                additionalText="(Select All That Apply)*"
                id="specialties"
                options={useMemo(() => Object.entries(Specialties).map(([value, label]) => ({
                  value,
                  label
                })), [])}
                error={errors.specialties?.message}
                required={true}
                {...register('specialties', getFormFieldRules('specialties'))}
              />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-6 mt-12">
          <span className="text-p_footnote text-black/70">
            *Required field
          </span>
          {hasSpecialtyErrors && (
            <span id={FORM_CONFIG.sections.specialty.errorId} className="text-p text-error">
              *Please fill all the required fields
            </span>
          )}
        </div>
      </section>
      {/* Facility Information Section */}
      <section 
        id={FORM_CONFIG.sections.facility.id} 
        aria-label="Facility Information" 
        className="bg-white rounded-all_48"
      >
        <div className="rounded-all_48 container-gradient-light" style={{ background: "linear-gradient(0deg, rgba(122, 197, 147, 0.30) 0%, rgba(122, 197, 147, 0.30) 100%), #FFF" }}>
        <div className="bg-white py-12 px-6 lg:p-14 rounded-all_48 flex flex-col gap-6">
          <h2 className="text-p_medium lg:text-h4_medium text-dark_green text-center" id={FORM_CONFIG.sections.facility.id}>
            {FORM_CONFIG.sections.facility.title}
          </h2>
          <p className="lg:text-p text-[14px] leading-[18px] text-regular text-center text-black/70">
              You may add up to 6 different facilities you are affiliated with to be displayed on the Surgeon Selector tool
          </p>
        </div>

        <div className="py-8 px-6 lg:p-14 flex flex-col gap-6">
          <h4 onClick={() => setShowFacilitySearch(!showFacilitySearch)} className="flex flex-row items-center justify-between text-p_medium lg:text-h4_medium text-dark_green cursor-pointer lg:gap-4 gap-6">
            Add a Hospital/Practice/Clinic ({confirmedFacilities.length}/6)
            <span className={`flex justify-center items-center !w-[32px] min-w-[32px] lg:!w-[56px] h-8 lg:h-14 rounded-all_48 bg-white/30 border-gradient-light`}>
              <img className={`${showFacilitySearch ? 'rotate-0' : 'rotate-180'} transition-all duration-500 ease-in-out`} src={facilitySearchDropdown} alt="info" />
            </span>
          </h4>
          {/* Add the error message for no facilities */}
          {errors.confirmedFacilities && (
            <div className="text-p_footnote text-error text-center lg:text-left mt-2 mb-14">
              {errors.confirmedFacilities.message}
            </div>
          )}
          {(!showFacilitySearch && confirmedFacilities.length < 6) && (
            <div className="flex flex-col gap-8 mb-6 transition-all duration-300 ease-in-out">
              <p className="text-black/70 mb-2 font-regular lg:text-p text-[14px] leading-[18px]">
                Use our <span className="font-bold">search tool</span> to find the facility you are looking for and we will fill out the rest!
              </p>
              {fields.map((field, index) => (
                <div 
                  key={field.id}
                  className={`facility-form-container ${isFacilityConfirmed(field.id) ? 'facility-confirmed hidden' : ''}`}
                >
                  <FacilityForm 
                    key={field.id} 
                    facilityId={index}
                    removeFacility={() => remove(index)}
                    isRemovable={fields.length > 1}
                    stateSelectRefs={stateSelectRefs}
                  />
                </div>
              ))}
            
              {confirmedFacilities.length < 6 && (
                <div className="flex flex-col gap-4">
                  <div className="flex lg:justify-start justify-center">
                    <SimpleButton 
                      type="button" 
                      onClick={handleConfirmAddress}
                      className={`bg-white/70 text-dark_green p-6`}
                      ariaLabel="Confirm address"
                      label="Confirm Address"
                    >
                      Confirm Address
                    </SimpleButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Agreements Section */}
        <section 
          id={FORM_CONFIG.sections.agreement.id} 
          aria-label="Agreements" 
          className="bg-white lg:p-14  px-6 py-14 rounded-all_48"
        >
          <div className={`flex flex-wrap gap-4 pb-12 ${confirmedFacilities.length > 0 ? 'visible' : 'hidden'}`} id="facility-list-cards">
            {confirmedFacilities.map((facility, index) => (
              <div 
                key={facility.id}
                className="max-w-[400px] flex flex-row bg-green/30 py-8 px-6 lg:pr-10 gap-4 rounded-all_48 facility-card border-gradient-light"
                data-facility-id={facility.id}
              >
                <span className="flex justify-center items-center text-h4 font-bold bg-orange text-dark_green w-[40px] h-10 overflow-hidden rounded-full">
                  {index + 1}
                </span>
                <div className="flex flex-col gap-4">
                    <h4 className="text-p font-bold text-dark_green">
                      {facility.facilityName}
                    </h4>
                    <p className="flex flex-row items-center gap-2.5 text-p font-regular text-black/70">
                    Affiliation identified <img src={checkIcon} alt="check" />
                    </p>
                    <div className="flex flex-row items-center gap-6">
                      <a className="text-p font-medium text-dark_green underline cursor-pointer" onClick={() => handleFullFacilityInfo(facility, index)}>
                        Full Facility Information
                      </a> 
                      <span className="separator text-orange">|</span>
                      <a className="text-p font-medium text-dark_green underline cursor-pointer" onClick={() => removeConfirmedFacility(facility.id)}>
                        Remove
                      </a>
                    </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-6">
            <Checkbox 
              label="I would like to receive information and updates from Pacira.*" 
              {...register('receiveUpdates', getFormFieldRules('receiveUpdates'))}
              error={errors.receiveUpdates?.message}
              id="receiveUpdates"
            />
            
            <Checkbox 
              label={
                <>
                  I agree to the terms and conditions of the Surgeon Selector as listed in the website <a href="https://www.pacira.com/terms-of-use/" target="_blank" className="text-dark_green font-bold underline inline-flex">Terms of Use</a>.*
                </>
              } 
              {...register('agreeToTerms', getFormFieldRules('agreeToTerms'))}
              error={errors.agreeToTerms?.message}
              id="agreeToTerms"
            />
            
            <Checkbox 
              label="By submitting my information for inclusion in the EXPAREL Surgeon Selector I acknowledge that I am an active user of EXPAREL® and that it is available at the affiliated facilities I have listed. I acknowledge and agree that no fees or remuneration of any kind have been or will be paid in exchange for inclusion in this directory. I understand that inclusion in this directory does not indicate an endorsement, recommendation or referral by Pacira of myself as a healthcare provider or the affiliated facilities listed. I acknowledge and agree that Pacira is not responsible and shall not be held liable for any harm or damage resulting from reliance on the information provided in this directory. I agree that Pacira reserves the right to remove my information from this directory in the event that I no longer meet the inclusion criteria.*" 
              {...register('agreeToSubmission', getFormFieldRules('agreeToSubmission'))}
              error={errors.agreeToSubmission?.message}
              id="agreeToSubmission"
            />
          </div>
          
          {hasAgreementErrors && (
            <div className="mt-4">
              <span id={FORM_CONFIG.sections.agreement.errorId} className="text-p text-error">
                *Please agree to all required terms
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-6 mt-4">
            <span className="text-p_footnote text-black/70">
              *Required field
            </span>
            {hasFacilityErrors && (
              <span id={FORM_CONFIG.sections.facility.errorId} className="text-p text-error">
                *Please fill all the required fields
              </span>
            )}
          </div>
        </section>

        </div>
      </section>

      {/* TODO: Status Section */}
      <section className="status-section hidden [&.active]:flex flex-col items-center text-center gap-6 rounded-all_48 bg-white lg:p-14 px-6 py-14">   {/* to do: add active class when status is active */}
          <h4 className="text-h4_medium text-dark_green">
            Facility Verification Status
          </h4>
          <p className="text-p font-regular text-black/70">We're having trouble confirming the facility information you entered.</p>

          <div className="flex flex-col justify-center items-center text-center gap-6 my-6 lg:p-8 p-6 rounded-all_48 bg-green/30 border-gradient-light">
            <p className="text-p font-bold mx-auto leading-5 text-dark_green lg:max-w-[60%]">Mayo Clinic - Crepeau Amy Zongolowicz MD</p>   {/* TODO: add facility name */}
            <p className="text-p font-regular mx-auto leading-5 text-black/70 lg:max-w-[60%]">5777 E Mayo Blvd, Phoenix, AZ, 85054</p>   {/* TODO: add facility address */}
          </div>
          <p className="text-p font-regular text-black/70 max-w-[854px] mb-6">
          Unfortunately, we are unable to verify your affiliation at this time. An affiliation request has been sent to the team and should be resolved within 48 hours. Should we require any additional information, your dedicated rep will reach out to you. Otherwise, we will alert you when your information is live on the website.
          </p>

          <p className="text-p font-regular text-black/70">In the meantime, if you have any questions, please contact your rep via their information below:</p>

          <div className="flex flex-col lg:justify-start justify-center lg:items-start items-center lg:text-left text-center gap-4 my-6 lg:p-0 p-4">
            <p className="text-p text-black/70 flex lg:flex-row flex-col">
              <span className="font-bold text-dark_green mr-2.5">Name:</span> 
              <span className="font-regular">Avery Schumaker</span>    {/* TODO: add rep name */}
            </p>
            <p className="text-p text-black/70 flex lg:flex-row flex-col">
              <span className="font-bold text-dark_green mr-2.5">Email Address:</span>
              <span className="font-regular">Avery.Schumaker@pacira.com</span>    {/* TODO: add rep email */}
            </p>
            <p className="text-p text-black/70 flex lg:flex-row flex-col">
              <span className="font-bold text-dark_green mr-2.5">Phone:</span>
              <span className="font-regular">(425) 999 - 2261</span>    {/* TODO: add rep phone */}
            </p>
          </div>

          <p className="text-p font-regular text-black/70 max-w-[854px]">Please note, if you use EXPAREL at another facility or believe there was an error on your submission, please login to edit your information. All verified facilities will populate in the search field dropdown.</p>
      </section>
      
      {/* Form Controls */}
      <div className="flex justify-center gap-6 lg:mt-6 mt-0">
        <CancelButton 
          onClick={handleCancelClick} 
          aria-label="Reset form"
        >
          Cancel
        </CancelButton>
        
        <SubmitButton 
          type="submit" 
          aria-label="Submit form"
        >
          Submit
        </SubmitButton>
      </div>
      <div className="flex flex-col gap-6 justify-center items-center lg:mt-6 mt-0">
        <p className="lg:text-p text-[14px] leading-[18px] text-white text-center lg:w-full w-[260px]"> To see how patients search on EXPAREL Surgeon Selector, <a href="https://www.exparel.com/exparel-surgeon-selector" target="_blank" className="underline font-bold">click here.</a></p>
        <p className="lg:text-p text-[14px] leading-[18px] text-white text-center lg:w-full w-[260px]">The information you submit will be governed by our <a target="_blank" href=" https://www.pacira.com/privacy-cookies-policy/ " className="underline font-bold">Privacy Notice</a>.</p>
      </div>

      {/* Reset Confirmation Modal */}
      <Modal 
        showModal={showResetConfirm} 
        closeModal={closeResetConfirm} 
        dialogPanelClassName="rounded-all_48 p-14"
      >
        <div className="flex flex-col gap-6 relative">
          <button 
            onClick={closeResetConfirm}
            className="absolute -top-8 -right-8 text-dark_green hover:text-black"
            aria-label="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h2 className="text-h4_medium font-bold text-dark_green">
            Are you sure you want to cancel the registration?
          </h2>
          <p className="text-p text-black/70">
            You are almost done! But don't worry, you can sign up again at any time.
          </p>
          <div className="flex gap-6 justify-end mt-4">
            <SimpleButton 
              type="button" 
              onClick={closeResetConfirm}
              className="bg-white/70 text-dark_green"
              ariaLabel="Cancel reset"
            >
              Continue
            </SimpleButton>
            <SimpleButton 
              type="button" 
              onClick={confirmReset}
              className="!bg-dark_green text-white"
              ariaLabel="Confirm reset"
            >
              Cancel Registration
            </SimpleButton>
          </div>
        </div>
      </Modal>

      {/* Facility Information Modal */}
      <Modal 
        showModal={showFacilityInfo} 
        closeModal={closeFacilityInfo} 
        dialogPanelClassName="rounded-all_48 p-12"
      >
        {selectedFacility && (
          <div className="flex flex-col gap-4 relative">
            <button 
              onClick={closeFacilityInfo}
              className="absolute -top-6 -right-6 text-dark_green"
              aria-label="Close modal"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h3 className="text-h5 font-bold text-dark_green mb-2">
              Location {selectedFacility.index} of 6
            </h3>
            <div className="flex flex-col gap-2 text-p text-black/70">
              <p>{selectedFacility.facilityName}</p>
              <p>{selectedFacility.address}</p>
              <p>{selectedFacility.city}, {selectedFacility.state} {selectedFacility.zipCode}</p>
              <p>{selectedFacility.phoneNumber}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StepTwo; 
